import * as React from 'react'

import { cn } from '@/registry/reapit/lib/utils'

// DS Tag (page 8403145): read-only metadata pill.
// Distinct from Chip (which is interactive/selectable).
// Tags are compact, non-interactive labels for classification and metadata.
// No hover or active states — visual weight is lighter than Chip.

type TagProps = React.ComponentProps<'span'> & {
  /** Truncate the label to a single line with an ellipsis instead of wrapping. */
  truncate?: boolean
  /** Limit the maximum width of the tag (CSS value, e.g. "12rem" or 192). */
  maxWidth?: string | number
}

function Tag({ className, truncate = false, maxWidth, style, children, ...props }: TagProps) {
  const inlineStyle: React.CSSProperties = {
    ...style,
    ...(maxWidth !== undefined ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth } : {}),
  }

  return (
    <span
      data-slot="tag"
      style={inlineStyle}
      className={cn(
        // DS: pill shape, neutral-lightest bg, tertiary text, small type (text-xs), medium weight
        // text-text-tertiary (neutral-700 / #415161) gives 8.3:1 contrast on neutral-lightest (WCAG AA)
        'inline-flex items-center rounded-full bg-fill-neutral-lightest px-2.5 py-0.5 text-xs font-medium text-text-tertiary',
        // Truncation is optional — default is overflow (single line)
        truncate ? 'max-w-full overflow-hidden truncate' : 'whitespace-nowrap',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

type TagGroupProps = React.ComponentProps<'div'> & {
  overflow?: 'wrap' | 'scroll'
}

function TagGroup({ className, overflow = 'wrap', ...props }: TagGroupProps) {
  return (
    <div
      data-slot="tag-group"
      data-overflow={overflow}
      className={cn(
        'flex gap-1.5',
        overflow === 'wrap' && 'flex-wrap',
        overflow === 'scroll' && 'overflow-x-auto scrollbar-none pb-0.5',
        className,
      )}
      {...props}
    />
  )
}

export { Tag, TagGroup }
export type { TagProps, TagGroupProps }
